/**
 * The feedback page's arithmetic-free model (#377): how the server's notes are grouped for the page-1 summary and the
 * closing table, and how a rung pull's response is folded into what is on screen.
 *
 * **The summary is a map of the work, not a measure of the learner** (`docs/feedback_research.md` principle 10). One
 * entry per question, in the order the questions first appear: what was proved (the ✓ notes' own wording, which is
 * assembled from what each check covers, so it can claim no more), the kind of gap (the ✗ notes' error type) and on
 * which pages, and whether anything was read but not checked. No status, score, count or proportion, and nothing about
 * what the learner understands.
 *
 * Principle 10 asks for one line per COURSE TOPIC with the course section. The sheet's topic is its Moodle section (#439): the
 * server resolves it and the summary opens with one line naming it (`topicLine`), the section's name or "Section n", with the
 * per-question entries, grouped by the question label the learner confirmed (#406 slice 3), under it. A question is not mapped
 * to a topic of its own (#366 sources rules, not a syllabus): the whole sheet has one. When the server cannot resolve the
 * topic (`topic` is null) the summary falls back to the per-question entries alone.
 */

import type { HomeworkFeedbackResponse } from '@davinci/contracts';
import { feedbackLabelsFor } from '../../i18n/homework-feedback-labels.js';
import type { UiLocale } from '../../i18n/locale.js';

/** The summary's topic line: the sheet's Moodle section by name, or "Section n" when it has none; null when unresolved. */
export function topicLine(topic: HomeworkFeedbackResponse['topic'], locale: UiLocale = 'en'): string | null {
  return topic === null ? null : (topic.sectionName ?? feedbackLabelsFor(locale).topicLineFallback(topic.sectionNumber));
}

export type FeedbackNote = HomeworkFeedbackResponse['notes'][number];

export interface QuestionSummary {
  /** The question label, or null for notes whose first line names no question. */
  readonly question: string | null;
  /** The ✓ notes' wording, each once, with the 1-based pages it is on, in order. */
  readonly proved: readonly { readonly text: string; readonly pages: readonly number[] }[];
  /** Each ✗ error type once, with the 1-based pages it is on, in order. */
  readonly gaps: readonly { readonly text: string; readonly pages: readonly number[] }[];
  /** The 1-based pages where a line was read and not checked (○), in order. */
  readonly notCheckedPages: readonly number[];
  /** Whether a not-checked line is one a course rule would let DaVinci check (`refused`). */
  readonly needsCourseRule: boolean;
  /** True when every note of the group is filed under a display hint (#646), not a recorded label: nothing recorded can be "fixed" there. */
  readonly hintOnly?: true;
}

/**
 * #646: a note with a `questionHint` (display only; the server sends it only where no label is recorded) is shown under that question
 * exactly as a note with a recorded label is, so every block, frame and the phone read `questionLabel`. The note keeps its `questionHint`,
 * which is how a guess is told from a record: nothing that is about the record (a "fixed" mark) fires on it. Same object when no note has one.
 */
export function withDisplayedHints<T extends { readonly notes: readonly FeedbackNote[] }>(feedback: T | undefined): T | undefined {
  if (feedback === undefined || !feedback.notes.some((note) => note.questionLabel === null && note.questionHint !== undefined)) return feedback;
  return {
    ...feedback,
    notes: feedback.notes.map((note) => (note.questionLabel === null && note.questionHint !== undefined ? { ...note, questionLabel: note.questionHint } : note)),
  };
}

const NOT_CHECKED = new Set<FeedbackNote['mark']>(['read_not_checked', 'refused', 'undecided', 'withheld']);

/**
 * True when NO claim of the run was decided (#646): every note is a line read and not checked, or a claim that reached no verdict. An
 * `on_request` note is a claim the response does not open, which may be a verdict, so it counts as possibly decided and this is false.
 */
export function nothingDecided(notes: readonly FeedbackNote[]): boolean {
  return notes.every((note) => NOT_CHECKED.has(note.mark));
}

function pushOnce<T>(list: T[], value: T): void {
  if (!list.includes(value)) list.push(value);
}

/**
 * The summary, one entry per question in first-appearance order; the no-question group last. `pageNumberOf` turns a page id
 * into its 1-based page number. `on_request` notes say nothing and are left out, so a closed feedback has no summary.
 */
export function summarise(notes: readonly FeedbackNote[], pageNumberOf: (pageId: string) => number): QuestionSummary[] {
  const groups = new Map<string | null, { proved: Map<string, number[]>; gaps: Map<string, number[]>; notChecked: number[]; needsRule: boolean; recorded: boolean }>();
  for (const note of notes) {
    if (note.mark === 'on_request') continue;
    let group = groups.get(note.questionLabel);
    if (group === undefined) {
      group = { proved: new Map(), gaps: new Map(), notChecked: [], needsRule: false, recorded: false };
      groups.set(note.questionLabel, group);
    }
    if (note.questionHint === undefined) group.recorded = true;
    const page = pageNumberOf(note.pageId);
    if (note.mark === 'proved' || note.mark === 'refuted') {
      const byText = note.mark === 'proved' ? group.proved : group.gaps;
      const pages = byText.get(note.text) ?? [];
      pushOnce(pages, page);
      byText.set(note.text, pages);
    } else if (NOT_CHECKED.has(note.mark)) {
      pushOnce(group.notChecked, page);
      if (note.mark === 'refused') group.needsRule = true;
    }
  }
  const ordered = [...groups.entries()].sort(([a], [b]) => (a === null ? 1 : 0) - (b === null ? 1 : 0));
  return ordered.map(([question, group]) => ({
    question,
    proved: [...group.proved.entries()].map(([text, pages]) => ({ text, pages })),
    gaps: [...group.gaps.entries()].map(([text, pages]) => ({ text, pages })),
    notCheckedPages: group.notChecked,
    needsCourseRule: group.needsRule,
    ...(question !== null && !group.recorded ? { hintOnly: true as const } : {}),
  }));
}

/** "page 2", "pages 1 and 3", "pages 1, 2 and 4": addresses, never a count. */
export function pagesPhrase(pages: readonly number[], locale: UiLocale = 'en'): string {
  return feedbackLabelsFor(locale).pagesPhrase(pages);
}

/**
 * The notes on screen after a rung pull. The pull's response opens only the pulled line (every other line comes back
 * `on_request`), so only that line's note is taken from it; every other note stays as the response that opened it showed
 * it. Nothing is re-shown that was not already on screen.
 */
export function withPulledNote(current: HomeworkFeedbackResponse, pulled: HomeworkFeedbackResponse, claimId: string): HomeworkFeedbackResponse {
  const fresh = pulled.notes.find((note) => note.claimId === claimId);
  if (fresh === undefined || pulled.run?.runId !== current.run?.runId) return pulled;
  return { ...current, notes: current.notes.map((note) => (note.claimId === claimId ? fresh : note)) };
}

/** Whether every checked line of a response is closed: it has been checked, and this response opened nothing. */
export function isClosed(feedback: HomeworkFeedbackResponse): boolean {
  return feedback.run !== null && feedback.notes.some((note) => note.mark === 'on_request');
}

/** The note for `claimId` as under review, once the server has filed the dispute (201): nothing else on screen changes. */
export function withDisputedNote(current: HomeworkFeedbackResponse, claimId: string, ruleBy: string, text: string): HomeworkFeedbackResponse {
  return {
    ...current,
    notes: current.notes.map((note) =>
      note.claimId === claimId
        ? { ...note, mark: 'under_review' as const, text, disputable: false, rungs: [], pullable: [], solutionOpensAt: null, certificate: null, covers: [], examPrepNoted: false, ruleBy }
        : note,
    ),
  };
}
